//
// # GenSymbolFromSchDialog
//
// Modal dialog for generating a symbol from the active schematic.
// Shows the source schematic path and lets the user define the
// symbol path (pre-filled with a default derived from the schematic).
//

import React, { useCallback, useEffect, useState } from "react";

import { Ident } from "GenAlphaModel";
import { schema } from "GenAlphaSchema";

import { Modal } from "../Modal/Modal";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";

// Validation: path segments must be alphanumeric, underscore, or hyphen.
const NAME_PATTERN = /^[a-zA-Z0-9_-]+$/;

function isValidSegment(name: string): boolean {
  return NAME_PATTERN.test(name);
}

// Derive a default symbol path from the schematic ident. A path holds exactly
// one design (its type is its contentKind), so the symbol can't reuse the
// schematic's path — pick a non-colliding sibling leaf. When the schematic uses
// the default leaf "schematic", swap it for "symbol" (echoing the old
// library/cell/view default); otherwise suffix "_symbol". The user can edit it
// before creating.
// e.g. ["lib", "cell", "schematic"] -> ["lib", "cell", "symbol"]
//      ["lib", "cell", "amp"]       -> ["lib", "cell", "amp_symbol"]
function defaultSymbolPath(schematicIdent: schema.Ident): string[] {
  if (schematicIdent.kind === "hierpath" && schematicIdent.path.length > 0) {
    const parts = [...schematicIdent.path];
    const leaf = parts[parts.length - 1];
    parts[parts.length - 1] =
      leaf === "schematic" ? "symbol" : `${leaf}_symbol`;
    return parts;
  }
  if (schematicIdent.kind === "libcellview") {
    // OA schematic → symbol keeps the familiar lib/cell/symbol form, matching
    // the layout default in platform.ts (doGenerateFromSource).
    return [schematicIdent.lib, schematicIdent.cell, "symbol"];
  }
  return ["symbol"];
}

export interface GenSymbolFromSchDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

// Sentinel for "no PDK" / pdk-agnostic selection.
const NO_PDK = "__none__";

export function GenSymbolFromSchDialog({
  open,
  onClose,
}: GenSymbolFromSchDialogProps) {
  const workspace = useWorkspace();
  const { activeIdent } = useWorkspaceSnapshot();

  // Form state: the symbol path segments joined as a string for editing
  const [symbolPath, setSymbolPath] = useState("");

  // PDK selection: defaults to the schematic's pdkId, or generic
  const [selectedPdk, setSelectedPdk] = useState<string>(NO_PDK);

  // UI state
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get the schematic's pdkId from the active editor content
  const activeContent = workspace.getActiveEditor()?.content;
  const schematicPdkId =
    activeContent?.contentKind === "schematic"
      ? activeContent.pdkId
      : undefined;

  // Reset form when dialog opens, pre-fill with default
  useEffect(() => {
    if (open && activeIdent) {
      setSymbolPath(defaultSymbolPath(activeIdent).join("/"));
      setSelectedPdk(schematicPdkId ?? NO_PDK);
      setError(null);
      setCreating(false);
    }
  }, [open, activeIdent, schematicPdkId]);

  // Validation
  const segments = symbolPath.split("/").filter((s) => s.length > 0);
  const pathValid =
    segments.length > 0 && segments.every((s) => isValidSegment(s));

  const canSubmit = pathValid && !creating;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !activeIdent) return;

    setCreating(true);
    setError(null);

    const symbolIdent: schema.HierPathIdent = {
      kind: "hierpath",
      path: segments,
    };

    const pdkId = selectedPdk !== NO_PDK ? selectedPdk : undefined;
    await workspace.genSymbolFromSchematic(symbolIdent, pdkId);

    // If we get here without error, the workspace method handled
    // status messages and opening the new symbol.
    setCreating(false);
    onClose();
  }, [canSubmit, activeIdent, segments, workspace, onClose]);

  const schematicDisplayName = activeIdent
    ? Ident.displayName(activeIdent)
    : "None";

  // Common input styles
  const inputClass =
    "w-full rounded border px-2.5 py-1.5 text-xs font-mono outline-none";
  const inputStyle = (valid: boolean) => ({
    backgroundColor: "var(--color-ide-bg)",
    borderColor: valid ? "var(--color-ide-border)" : "var(--color-ide-error)",
    color: "var(--color-ide-text)",
  });

  return (
    <Modal open={open} onClose={onClose} title="Generate Symbol from Schematic">
      <div className="flex flex-col gap-3">
        {/* Source schematic (read-only) */}
        <div>
          <label
            className="block text-[10px] font-semibold uppercase tracking-widest mb-1"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Source Schematic
          </label>
          <div
            className={inputClass}
            style={{
              backgroundColor: "var(--color-ide-bg)",
              borderColor: "var(--color-ide-border)",
              color: "var(--color-ide-text-dim)",
            }}
          >
            {schematicDisplayName}
          </div>
        </div>

        {/* Symbol path (editable) */}
        <div>
          <label
            className="block text-[10px] font-semibold uppercase tracking-widest mb-1"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Symbol Path
          </label>
          <input
            type="text"
            placeholder="library/cell/symbol"
            value={symbolPath}
            onChange={(e) => setSymbolPath(e.target.value)}
            className={inputClass}
            style={inputStyle(pathValid || symbolPath.length === 0)}
            autoFocus
          />
          {!pathValid && symbolPath.length > 0 && (
            <p
              className="text-[10px] mt-0.5"
              style={{ color: "var(--color-ide-error)" }}
            >
              Path segments: letters, numbers, underscores, and hyphens only
            </p>
          )}
        </div>

        {/* PDK selection */}
        <div>
          <label
            className="block text-[10px] font-semibold uppercase tracking-widest mb-1"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            PDK
          </label>
          <select
            value={selectedPdk}
            onChange={(e) => setSelectedPdk(e.target.value)}
            className={inputClass}
            style={{
              backgroundColor: "var(--color-ide-bg)",
              borderColor: "var(--color-ide-border)",
              color: "var(--color-ide-text)",
            }}
          >
            {schematicPdkId && (
              <option value={schematicPdkId}>{schematicPdkId}</option>
            )}
            <option value={NO_PDK}>None (PDK-agnostic)</option>
          </select>
        </div>

        {/* Error banner */}
        {error && (
          <div
            className="rounded border px-3 py-2 text-xs"
            style={{
              backgroundColor: "rgba(241, 76, 76, 0.1)",
              borderColor: "var(--color-ide-error)",
              color: "var(--color-ide-error)",
            }}
          >
            {error}
          </div>
        )}

        {/* Footer buttons */}
        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs rounded border"
            style={{
              backgroundColor: "var(--color-ide-bg)",
              borderColor: "var(--color-ide-border)",
              color: "var(--color-ide-text-muted)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            className="px-3 py-1.5 text-xs rounded border"
            style={{
              backgroundColor: canSubmit
                ? "var(--color-ide-accent)"
                : "var(--color-ide-bg)",
              borderColor: canSubmit
                ? "var(--color-ide-accent)"
                : "var(--color-ide-border)",
              color: canSubmit ? "#fff" : "var(--color-ide-text-dim)",
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
          >
            {creating ? "Generating..." : "Generate"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
